/**
 * Runtime translation overlay provider.
 *
 * Pre-auth TanStack Query provider that fetches the unauthenticated
 * `GET /public/translations` boot endpoint and merges the tenant's
 * AI-generated languages + per-tenant overrides into i18next at runtime.
 *
 * Ordering matters: AI-generated languages are applied FIRST (so their
 * resource bundles exist), then overrides are layered on top so overrides
 * always win over both base and generated strings.
 *
 * The endpoint never 500s; on any error `data` is empty-shaped and the overlay
 * is a no-op (bundled base JSON renders, i18next `fallbackLng: 'en'` covers
 * gaps). Re-applying via `refresh()` is idempotent (`addResourceBundle` runs
 * with deep-merge + overwrite).
 */

import {
  createContext,
  useContext,
  useEffect,
  type ReactNode,
} from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from './api';
import { queryKeys } from './query-client';
import {
  BASE_LANGUAGES,
  addDynamicLanguage,
  applyTranslationOverrides,
  resetBundledLanguages,
  getCurrentLanguage,
} from './i18n';
import i18n from './i18n';
import { PageLoading } from '../components/Loading';

interface PublicTranslations {
  overrides: Record<string, Record<string, string>>;
  enabledLanguages: string[];
  generatedTranslations: Record<string, Record<string, Record<string, string>>>;
}

interface TranslationsContextValue {
  isLoading: boolean;
  refresh: () => void;
}

const TranslationsContext = createContext<TranslationsContextValue>({
  isLoading: true,
  refresh: () => {},
});

export function TranslationsProvider({ children }: { children: ReactNode }) {
  const savedLang = getCurrentLanguage();
  const isNonBaseSaved = !BASE_LANGUAGES.includes(savedLang);

  const { data, isLoading, refetch } = useQuery({
    queryKey: queryKeys.publicTranslations(),
    queryFn: async () =>
      (await apiClient.get<{ data: PublicTranslations }>('/public/translations')).data,
    staleTime: Infinity,
    gcTime: Infinity,
  });

  useEffect(() => {
    if (!data) return;
    // 0) Restore bundled base languages first so removed overrides do not
    //    linger from a previous overlay application (idempotent re-apply).
    resetBundledLanguages();
    // 1) AI-generated languages next (registers their resource bundles).
    Object.entries(data.generatedTranslations).forEach(([lng, byNs]) => {
      addDynamicLanguage(lng, byNs);
    });
    // 2) overrides on top (win over base + generated).
    applyTranslationOverrides(data.overrides);
    // A non-base saved language only exists after the overlay applies, so make
    // sure it is active now that its bundles are registered.
    if (isNonBaseSaved && i18n.language !== savedLang) {
      void i18n.changeLanguage(savedLang);
    }
  }, [data, isNonBaseSaved, savedLang]);

  // Base-language users render immediately (an override-only flash is
  // acceptable per plan). A non-base saved language would flash raw keys, so
  // hold render until the overlay has been applied.
  if (isNonBaseSaved && isLoading) return <PageLoading />;

  return (
    <TranslationsContext.Provider value={{ isLoading, refresh: () => void refetch() }}>
      {children}
    </TranslationsContext.Provider>
  );
}

export function useTranslations(): TranslationsContextValue {
  return useContext(TranslationsContext);
}
